<template>
  <v-slide-x-reverse-transition>
    <v-card class="elevation-5 provider-group-details vop-bg-whisper" v-show="isVisible">
      <div>
        <v-toolbar flat class="vop-bg-whisper">
          <v-toolbar-title class="subheading ttu font-weight-medium">
            <span v-if="isEditMode"> {{ messages.label.EDIT_XPATH_MAPPING }}</span>
            <span v-else>{{ messages.label.ADD_XPATH_MAPPING }}</span>
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="hide()">
            <v-icon medium>close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text>
          <v-layout wrap>
            <v-flex xs12 v-if="isEditMode">
              <v-alert :value="true" class="black--text text--lighten-2" type="warning">
                Changes to XPath mapping does not effect existing conditions / actions
              </v-alert>
            </v-flex>
            <v-flex xs12 v-if="xpathMappingError !== ''">
              <v-alert :value="true" type="error">
                {{ xpathMappingError }}
              </v-alert>
            </v-flex>
            <v-flex xs12>
              <v-form>
                <!-- Title -->
                <v-flex xs11>
                  <v-text-field v-model="xpathMapping.title" :clearable="!isEditMode" v-validate="'required|max:500'"
                    :error-messages="errors.collect('title')" data-vv-name="title" data-vv-delay="500" :counter="500"
                    :label="messages.label.TITLE" required>
                  </v-text-field>
                </v-flex>
                <!-- Xpath Mapping Location -->
                <v-flex xs11>
                  <v-textarea v-model="xpathMapping.xpathLocation" :clearable="!isEditMode"
                    v-validate="'required|max:3000'" :error-messages="errors.collect('xpathLocation')"
                    data-vv-name="xpathLocation" data-vv-delay="500" :counter="3000"
                    :label="messages.label.XPATH_LOCATION" required>
                  </v-textarea>
                </v-flex>
                <!-- Rule Enabled -->
                <v-flex xs4>
                  <span class="subheading">{{ messages.label.ENABLED }}</span>
                  <v-switch v-model="pathEnabled" color="primary" class="mt-0" :label="`${pathEnabled.toString()}`">
                  </v-switch>
                </v-flex>
              </v-form>
            </v-flex>
          </v-layout>
        </v-card-text>
      </div>
      <v-card-actions class="px-3">
        <v-spacer />
        <!-- Delete Provider Group template -->
        <v-dialog v-model="deleteDialog" width="500" v-if="isEditMode">
          <v-btn color="error" slot="activator">
            {{ messages.label.DELETE }}
          </v-btn>
          <v-card>
            <v-card-title class="subheading ttu">
              {{ messages.label.DELETE_XPATH_MAPPING }}
            </v-card-title>
            <v-card-text>
              <p>{{ messages.info.DELETE_XPATH_1 }} <b>{{ xpathMapping.title }}</b> ? </p>
              <p v-html="messages.info.DELETE_XPATH_2"></p>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="grey lighten-1 white--text" @click="deleteDialog = false">
                {{ messages.label.CANCEL }}
              </v-btn>
              <v-btn color="error" :disabled="deletingXPathMapping" :loading="deletingXPathMapping"
                @click="deleteXPathMapping">
                Yes
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-btn color="primary" class="ml-2" :disabled="updatingXPathMapping" :loading="updatingXPathMapping"
          v-if="isEditMode" @click="updateXPathMapping">
          {{ messages.label.SAVE }}
        </v-btn>
        <v-btn color="primary" class="ml-2" :disabled="addingXPathMapping" :loading="addingXPathMapping"
          @click="addXPathMapping" v-else>
          {{ messages.label.ADD }}
        </v-btn>
      </v-card-actions>
      <!-- Snackbar -->
      <v-flex xs12>
        <v-snackbar :bottom="true" :timeout="snackbar.timeout" :color="snackbar.color" v-model="snackbar.show">
          {{ snackbar.message }}
          <v-btn flat color="secondary" @click.native="snackbar.show = false">
            {{ messages.label.OK }}
          </v-btn>
        </v-snackbar>
      </v-flex>
    </v-card>
  </v-slide-x-reverse-transition>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex"
import messages from "@/MetadataRules/messages.json"
import MetadataRulesService from "@/MetadataRules/MetadataRulesService"
import * as metadataRulesDataService from "@/MetadataRules/MetadataRulesDataService"
import { GET_XPATH_MAPPINGS } from "@/store/constants/action-types"
import { MVATS_WEB_SERVICE_URL, RULE_MGMT_DATA_SERVICE_URL } from "@/store/constants/getter-types"
import AppService from "@/AppService"

/**
 * initialData
 */
function initialData(instance) {
  return {
    isEditMode: false,
    isVisible: false,
    valid: false,
    pathEnabled: true,
    xpathMapping: {},
    xpathMappingError: "",
    addingXPathMapping: false,
    updatingXPathMapping: false,
    deletingXPathMapping: false,
    deleteDialog: false,
    dictionary: instance.getValidationDictionary(),
    snackbar: {
      show: false,
      timeout: 5000,
      message: "",
      color: "error"
    }
  }
}

export default {
  name: "ProviderGroupDetails",
  $_veeValidate: {
    validator: "new"
  },
  data() {
    return initialData(this)
  },
  computed: {
    ...mapGetters([
      MVATS_WEB_SERVICE_URL, RULE_MGMT_DATA_SERVICE_URL
    ]),
    ...mapState([
      "mpxParentAccount"
    ])
  },
  methods: {
    ...mapActions("metadataRules", [
      GET_XPATH_MAPPINGS
    ]),

    /**
     * reset
     */
    reset() {
      Object.assign(this.$data, initialData(this))
    },

    /**
     * Hide side sheet view
     */
    hide() {
      this.isVisible = false
      this.resetXPathMppingData()
      MetadataRulesService.$emit("event:hideXPathMappingDetailsSheet")
    },

    /**
     * Show side sheet view
     */
    show(xpathMapping) {
      this.resetXPathMppingData()
      // Wait before updating form
      this.$nextTick(() => {
        this.$validator.reset()
        if (!this.$lodash.isUndefined(xpathMapping)) {
          this.isEditMode = true
          this.xpathMapping = this.$lodash.cloneDeep(xpathMapping)
          this.pathEnabled = !this.xpathMapping.disabled
        } else {
          this.isEditMode = false
          this.pathEnabled = true
        }
        this.isVisible = true
      })
    },

    /**
     * Validation messages for vee-validate
     */
    getValidationDictionary() {
      return {
        custom: {
          title: {
            required: () => "Title can't be empty",
            max: "Title may not be greater than 500 characters"
          },
          xpathLocation: {
            required: () => "XPath Location can't be empty",
            max: "XPath Location may not be greater than 3000 characters"
          }
        }
      }
    },
    /**
     * Re-initialize
     */
    resetXPathMppingData() {
      this.xpathMapping = {}
      this.xpathMappingError = ""
    },

    /**
     * retrieveXPathMappings
     */
    retrieveXPathMappings() {
      let queryObject = {
        sort: "title",
        account: this.mpxParentAccount.id
      }
      this.GET_XPATH_MAPPINGS(queryObject)
    },

    /**
     * Add new provider group
     */
    addXPathMapping() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.addingXPathMapping = true
          // TODO: Fix 'scheme' hard coding one we know how to retrieve list of schemes
          this.xpathMapping.scheme = "scheme"
          this.xpathMapping.disabled = !this.pathEnabled
          return metadataRulesDataService
            .createXPathMapping(this.xpathMapping,
              this.mpxParentAccount.id,
              this.RULE_MGMT_DATA_SERVICE_URL)
            .then((response) => {
              this.resetXPathMppingData()
              MetadataRulesService.$emit("event:refreshXpathMappingsList", "add", response.data.id)
              // Snack bar
              this.snackbar.show = true
              this.snackbar.color = "success"
              this.snackbar.message = this.messages.info.XPATH_CREATE_SUCCESS
              return response
            })
            .catch((error) => {
              let response = error.response.data
              if (response.isException) {
                this.xpathMappingError = response.description
              }
              return Promise.reject(error)
            })
            .finally(() => {
              this.addingXPathMapping = false
              this.retrieveXPathMappings()// Update store
            })
        }
      })
    },

    /**
     * Update provider group details
     */
    updateXPathMapping() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.updatingXPathMapping = true
          this.xpathMapping.disabled = !this.pathEnabled
          return metadataRulesDataService
            .updateXPathMapping(this.xpathMapping,
              this.mpxParentAccount.id,
              this.RULE_MGMT_DATA_SERVICE_URL)
            .then((response) => {
              MetadataRulesService.$emit("event:refreshXpathMappingsList", "update", this.xpathMapping.id)
              this.resetXPathMppingData()
              // Snackbar
              this.snackbar.show = true
              this.snackbar.color = "success"
              this.snackbar.message = this.messages.info.XPATH_UPDATE_SUCCESS
              return response
            })
            .catch((error) => {
              let response = error.response.data
              if (response.isException) {
                this.xpathMappingError = response.description
              }
              return Promise.reject(error)
            })
            .finally(() => {
              this.updatingXPathMapping = false
              this.retrieveXPathMappings()// Update store
            })
        }
      })
    },

    /**
     * Delet provider group
     */
    deleteXPathMapping() {
      this.deletingXPathMapping = true
      let xpathMappingId = this.xpathMapping.id.split("/").pop()
      return metadataRulesDataService
        .deleteXPathMapping(xpathMappingId,
          this.RULE_MGMT_DATA_SERVICE_URL)
        .then((response) => {
          this.isVisible = false
          this.resetXPathMppingData()
          MetadataRulesService.$emit("event:refreshXpathMappingsList", "delete")
          // Snackbar
          this.snackbar.show = true
          this.snackbar.color = "success"
          this.snackbar.message = this.messages.info.XPATH_DELETE_SUCCESS
          return response
        })
        .catch((error) => {
          let response = error.response.data
          if (response.isException) {
            this.providerGroupError = response.description
          }
          return Promise.reject(error)
        })
        .finally(() => {
          this.deleteDialog = false
          this.deletingXPathMapping = false
          this.retrieveXPathMappings()// Update store
        })
    }
  },
  mounted() {
    this.$validator.localize("en", this.dictionary)
  },
  created() {
    /**
     * Non reactive properties
     */
    this.messages = messages
    /**
     * Events
     */
    MetadataRulesService.$on("event:showXPathMappingDetailsSheet", this.show)
    AppService.$on("event:mpxAccountChanged", this.reset)
  },
  beforeDestroy() {
    MetadataRulesService.$off("event:showXPathMappingDetailsSheet", this.show)
    AppService.$off("event:mpxAccountChanged", this.reset)
  }
}
</script>

<style lang="scss">
.provider-group-details {
  min-height: 50%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
</style>



// WEBPACK FOOTER //
// src/MetadataRules/XpathMapping/XPathDetails.vue